import { atomWithStorage } from "jotai/utils";

// Configuration
export const MAX_RECENT_SUBTITLES = 5;

interface RecentSubtitle {
  id: string;
  content: string;
  name: string;
  timestamp: number;
  captionCount?: number;
  duration?: number;
}

// Simple hash function that works with Unicode strings
const simpleHash = (str: string): string => {
  let hash = 0;
  if (str.length === 0) return hash.toString();

  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  return Math.abs(hash).toString(36);
};

// Atom for recent subtitles stored in localStorage
export const recentSubtitlesAtom = atomWithStorage<RecentSubtitle[]>("recent-subtitles", [], {
  getItem: (key) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : [];
    } catch {
      return [];
    }
  },
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Handle localStorage errors gracefully
      console.warn("Failed to save recent subtitles to localStorage");
    }
  },
  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      console.warn("Failed to remove recent subtitles from localStorage");
    }
  },
});

// Function to add a new subtitle to recent list
export const addRecentSubtitle = (
  content: string,
  name?: string,
  captionCount?: number,
  duration?: number
) => {
  const subtitle: RecentSubtitle = {
    id: crypto.randomUUID(),
    content,
    name: name || `Subtitle ${new Date().toLocaleString()}`,
    timestamp: Date.now(),
    captionCount,
    duration,
  };

  return (prev: RecentSubtitle[]) => {
    // Remove duplicates based on content hash
    const contentHash = simpleHash(content);
    const filtered = prev.filter((item) => simpleHash(item.content) !== contentHash);

    // Add new subtitle at the beginning
    const updated = [subtitle, ...filtered];

    // Keep only the most recent based on MAX_RECENT_SUBTITLES
    return updated.slice(0, MAX_RECENT_SUBTITLES);
  };
};

// Function to remove a subtitle from recent list
export const removeRecentSubtitle = (id: string) => {
  return (prev: RecentSubtitle[]) => prev.filter((item) => item.id !== id);
};
